import type { CSSProperties } from 'react';
import { cn } from './cn';
import { useDelayedFlag } from './hooks';

export interface SkeletonProps {
  variant?: 'text' | 'stat' | 'row' | 'block';
  /** Líneas para `text`. */
  lines?: number;
  width?: CSSProperties['width'];
  height?: CSSProperties['height'];
  className?: string;
  /** Mostrar solo si la carga dura más de 300 ms (evita parpadeos). Por defecto true. */
  delayed?: boolean;
}

function Bone({ className, style }: { className?: string; style?: CSSProperties }) {
  return <span aria-hidden className={cn('atte-skeleton block animate-pulse-soft rounded-xs bg-active', className)} style={style} />;
}

/** Bloques de carga (§6.24). Pulso de 1,6 s, estático con movimiento reducido. */
export function Skeleton({ variant = 'block', lines = 3, width, height, className, delayed = true }: SkeletonProps) {
  const shown = useDelayedFlag(true, delayed ? 300 : 0);
  if (delayed && !shown) return <span className="sr-only">Cargando…</span>;
  const label = <span className="sr-only">Cargando…</span>;
  switch (variant) {
    case 'text':
      return (
        <div className={cn('flex flex-col gap-2', className)} role="status">
          {Array.from({ length: lines }, (_, i) => (
            <Bone key={i} className="h-3" style={{ width: i === lines - 1 && lines > 1 ? '60%' : (width ?? '100%') }} />
          ))}
          {label}
        </div>
      );
    case 'stat':
      return (
        <div className={cn('flex flex-col gap-2', className)} role="status">
          <Bone className="h-3 w-20" />
          <Bone className="h-6 w-28" />
          {label}
        </div>
      );
    case 'row':
      return (
        <div className={cn('flex h-11 items-center gap-4', className)} role="status">
          <Bone className="h-3 w-1/4" />
          <Bone className="h-3 w-1/5" />
          <Bone className="ml-auto h-3 w-16" />
          {label}
        </div>
      );
    default:
      return (
        <div role="status" className={className}>
          <Bone style={{ width: width ?? '100%', height: height ?? 80 }} className="rounded-sm" />
          {label}
        </div>
      );
  }
}

/** Pieza suelta de esqueleto (para armar formas propias). */
export function SkeletonBone(props: { className?: string; style?: CSSProperties }) {
  return <Bone {...props} />;
}
